import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ReportResourceNavigation } from "@via/pages/projects/runs/reports/report-resource-navigation.tsx";
import { ReportViewer } from "@via/pages/projects/runs/reports/report-viewer.tsx";
import { ERROR, RunTabs } from "@via/utils/constants.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import { type GetRunDetailsResponseBody, type RunReportResource } from "common";
import { JSX, useRef, useState, useEffect } from "react";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";
import PanelResizer from "@via/components/panel-resizer/panel-resizer.tsx";
import {
  findFirstFile,
  findReportInTree,
} from "@via/pages/projects/runs/reports/utils/helpers.ts";

interface ReportTreeProps {
  reportTree: RunReportResource[];
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  openByDefault: boolean;
  activeRunTab?: RunTabs;
}

export function ReportBrowser({
  reportTree,
  runSettingsData,
  isRunSettingsLoading,
  isRunSettingsError,
  openByDefault,
  activeRunTab,
}: ReportTreeProps): JSX.Element {
  const [selectedReport, setSelectedReport] = useState<
    RunReportResource | undefined
  >(reportTree?.length > 0 ? findFirstFile(reportTree) : undefined);

  const [isPanelCollapsed, setPanelCollapsed] = useState(false);
  const panelRef = useRef<ImperativePanelHandle | null>(null);

  useEffect(() => {
    if (reportTree.length > 0) {
      // If no selected report or the selected report doesn't exist in the new tree, find a new one
      const currentSelectedReportExists =
        selectedReport && findReportInTree(reportTree, selectedReport.id);

      if (!selectedReport || !currentSelectedReportExists) {
        setSelectedReport(findFirstFile(reportTree) || undefined);
      }
    }
  }, [reportTree, selectedReport]);

  const handleReportSelect = (report: RunReportResource) => {
    if (report?.extension !== "dir") {
      setSelectedReport(report);
    }
  };

  return (
    <>
      {isRunSettingsLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isRunSettingsError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div className="flex flex-row overflow-hidden">
          <PanelGroup direction="horizontal">
            <Panel
              ref={panelRef}
              collapsible={true}
              onCollapse={() => setPanelCollapsed(true)}
              onExpand={() => setPanelCollapsed(false)}
              defaultSize={20}
              maxSize={100}>
              <ReportResourceNavigation
                reportTree={reportTree}
                onReportSelect={handleReportSelect}
                selectedReport={selectedReport}
                openByDefault={openByDefault}
              />
            </Panel>
            <PanelResizer
              panelRef={panelRef}
              isPanelCollapsed={isPanelCollapsed}
            />
            <Panel minSize={30}>
              {selectedReport ? (
                <ReportViewer
                  runUUID={runSettingsData.runUUID as string}
                  report={selectedReport}
                  reportTree={reportTree}
                  activeRunTab={activeRunTab}
                />
              ) : (
                <div className="via-generic-error">
                  <EmptyDataIcon />
                  <p>No report selected or available to display.</p>
                </div>
              )}
            </Panel>
          </PanelGroup>
        </div>
      )}
    </>
  );
}
